import React, { useContext, useEffect, useState } from 'react';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import * as Yup from 'yup';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import 'primeflex/primeflex.css';
import { ToastContext } from 'app/context/ToastContext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { getAssistant, saveAssistant, updateAssistant } from 'app/api/assistantApi';
import { Checkbox } from 'primereact/checkbox';

// Define Yup validation schema
const validationSchema = Yup.object({
  firstName: Yup.string().max(255, 'Max 255 znakov').required('Meno je povinné'),
  lastName: Yup.string().max(255, 'Max 255 znakov').required('Priezvisko je povinné'),
});

const CreateAssistant: React.FC = () => {
  const { id } = useParams<{ id?: string }>(); // Capture id from URL

  const [searchParamsInitial] = useSearchParams();
  const tempParameters = searchParamsInitial.get('p');

  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const { showSuccess, showError } = useContext(ToastContext);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (id) {
      getAssistant(id)
        .then(response => {
          if (response.data) {
            setData(response.data);
          }
          setLoading(false);
        })
        .catch(error => {
          console.error(error);
          showError('Chyba', error.response?.data?.detail);
        });
    } else {
      setLoading(false);
    }
  }, []);

  // Handle form submission
  const handleSubmit = (values: any) => {
    setIsSaving(true);

    let call;
    if (id) {
      call = updateAssistant(id, values);
    } else {
      call = saveAssistant(values);
    }

    call
      .then(response => {
        showSuccess('Úspech', 'Asistent úspešne uložený.');
        setData(response.data);
        navigate(`/assistants?p=${tempParameters}`);
      })
      .catch(error => {
        showError('Chyba', error.response?.data?.detail);
        console.error('Error creating agent:', error);
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <div className="card">
      {loading && (
        <div className="flex justify-content-center">
          <ProgressSpinner />
        </div>
      )}

      {!loading && (
        <>
          <h2>{id ? 'Upraviť dáta asistenta' : 'Vytvoriť nového asistenta'}</h2>
          <Formik
            initialValues={
              data
                ? data
                : {
                    firstName: '',
                    lastName: '',
                    email: '',
                    password: '',
                  }
            }
            validationSchema={validationSchema}
            onSubmit={values => {
              handleSubmit(values);
            }}
          >
            {({ isSubmitting, setFieldValue, values, errors }) => {
              return (
                <Form onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} className="p-fluid">
                  <div className="col-12 md:col-6">
                    <div className="p-field">
                      <label style={{ marginRight: '10px' }} htmlFor="activated">
                        Aktivovaný
                      </label>
                      <Checkbox
                        name="activated"
                        onChange={e => void setFieldValue('activated', e.checked)}
                        checked={values.activated}
                      ></Checkbox>
                    </div>
                  </div>
                  <div className="col-12 md:col-6">
                    <div className="p-field">
                      <label htmlFor="firstName">Prvé meno</label>
                      <Field name="firstName" as={InputText} />
                      <ErrorMessage name="firstName" component="div" className="p-error" />
                    </div>
                  </div>
                  <div className="col-12 md:col-6">
                    <div className="p-field">
                      <label htmlFor="lastName">Priezvisko</label>
                      <Field name="lastName" as={InputText} />
                      <ErrorMessage name="lastName" component="div" className="p-error" />
                    </div>
                  </div>
                  <div className="col-12 md:col-6">
                    <div className="p-field">
                      <label htmlFor="email">E-mail</label>
                      <Field name="email" as={InputText} />
                      <ErrorMessage name="email" component="div" className="p-error" />
                    </div>
                  </div>
                  <div className="col-12 md:col-4">
                    <div className="p-field">
                      <label htmlFor="password">Heslo</label>
                      <Field type="password" name="password" as={InputText} />
                      <ErrorMessage name="password" component="div" className="p-error" />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    style={{ backgroundColor: '#e31818' }}
                    type="submit"
                    label="Uložiť"
                    icon="pi pi-check"
                    className="p-mt-3"
                    disabled={isSaving}
                  />
                  <Button
                    type="button"
                    style={{ marginTop: '10px', backgroundColor: 'gray' }}
                    label="Naspäť"
                    icon="pi pi-arrow-left"
                    className="p-button-secondary"
                    onClick={() => navigate(`/assistants?p=${tempParameters}`)}
                  />
                </Form>
              );
            }}
          </Formik>
        </>
      )}
    </div>
  );
};

export default CreateAssistant;
